import type { Theme, ThemeAssetEntry } from '../theme';
import { resolveEntryUrl } from '../asset/AssetManifestClient';

/**
 * Web Audio API wrapper for the engine's sound effects + biome
 * music + ambient layers. Loads audio buffers from theme bundles,
 * plays them on demand, and runs a master gain so mute toggles
 * apply to every channel uniformly.
 *
 * <p>Three playback surfaces:
 *
 * <ul>
 *   <li>{@link play} — fire-and-forget SFX (UI taps, reel stops,
 *       win stings). Each call spins up a fresh BufferSourceNode.</li>
 *   <li>{@link playMusic} / {@link stopMusic} — single looping music
 *       slot. Cross-fades when called with a different alias.</li>
 *   <li>{@link playAmbient} / {@link stopAmbient} — single looping
 *       ambient slot, runs alongside music.</li>
 * </ul>
 *
 * <p><strong>Autoplay policy.</strong> Browsers suspend the
 * AudioContext until a user gesture. Hosts call {@link resume} on
 * the first input (tap / keypress). Music + ambient {@code play*}
 * calls made before that point are <em>queued</em> and started
 * automatically the moment the context resumes — so the scene can
 * fire {@link playMusic} during preload without worrying about
 * gesture timing.
 *
 * <p><strong>Mute semantics.</strong> {@link setMuted} ramps the
 * master gain to 0 instead of just gating new calls, so already-
 * playing music + ambient drop out cleanly. Unmute ramps back to
 * {@link masterVolume}.
 */
/** A looping sound effect started by {@link AudioBus.playLoop}. */
export interface SfxLoop {
  /** Ramp its volume (0..1) over {@code ms}. */
  setVolume(volume: number, ms?: number): void;
  /** Fade it out over {@code fadeOutMs} and stop. Safe to call twice. */
  stop(fadeOutMs?: number): void;
}

export class AudioBus {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly registered = new Set<string>();
  private muted = false;
  private masterVolume = 1;

  // Looping music + ambient state. Only one of each at a time;
  // calling playMusic with a different alias cross-fades.
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private currentMusicAlias: string | null = null;
  private pendingMusic: { alias: string; opts: LoopPlayOptions } | null = null;

  private ambientSource: AudioBufferSourceNode | null = null;
  private ambientGain: GainNode | null = null;
  private currentAmbientAlias: string | null = null;
  private pendingAmbient: { alias: string; opts: LoopPlayOptions } | null = null;

  /** Lazily create the AudioContext on first use. Browsers don't permit
   * playback until {@link resume} is called from a user gesture. */
  private ensureContext(): AudioContext {
    if (!this.context) {
      this.context = new AudioContext();
      this.masterGain = this.context.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.masterVolume;
      this.masterGain.connect(this.context.destination);
    }
    return this.context;
  }

  /** Producer toggle for the music + ambient channels (independent of
   *  master mute). When {@code false} every {@link playMusic} +
   *  {@link playAmbient} call no-ops, and any currently-playing
   *  music/ambient fades out. Re-enabling doesn't auto-restart —
   *  the next scene mount's playMusic call fires normally. */
  private musicAllowed = true;

  setMusicEnabled(enabled: boolean): void {
    if (this.musicAllowed === enabled) return;
    this.musicAllowed = enabled;
    if (!enabled) {
      this.stopMusic({ fadeOutMs: 500 });
      this.stopAmbient({ fadeOutMs: 500 });
    } else if (this.lastMusicAlias) {
      this.startLoop('music', this.lastMusicAlias, { fadeInMs: 500 });
      if (this.lastAmbientAlias) {
        this.startLoop('ambient', this.lastAmbientAlias, { fadeInMs: 500 });
      }
    }
  }

  isMusicEnabled(): boolean {
    return this.musicAllowed;
  }

  /** Producer toggle for sound effects — the one-shot {@link play} cues
   *  (spins, wins, taps, coins). Independent of master mute AND the
   *  music channel, so a player can keep music playing while silencing
   *  the SFX (or vice-versa). When {@code false} every {@link play} call
   *  no-ops; looping music/ambient are unaffected. */
  private sfxAllowed = true;

  setSfxEnabled(enabled: boolean): void {
    this.sfxAllowed = enabled;
  }

  isSfxEnabled(): boolean {
    return this.sfxAllowed;
  }

  /** Remembered last aliases so {@link setMusicEnabled}(true) can
   *  restart what was playing before the toggle off. */
  private lastMusicAlias: string | null = null;
  private lastAmbientAlias: string | null = null;

  /** Resume the context after a user gesture. Safe to call repeatedly.
   * If music / ambient were queued during the suspended window, start
   * them now. */
  async resume(): Promise<void> {
    const ctx = this.ensureContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    if (this.pendingMusic) {
      const { alias, opts } = this.pendingMusic;
      this.pendingMusic = null;
      this.startLoopNow('music', alias, opts);
    }
    if (this.pendingAmbient) {
      const { alias, opts } = this.pendingAmbient;
      this.pendingAmbient = null;
      this.startLoopNow('ambient', alias, opts);
    }
  }

  /** Register and load every audio entry from the theme's bundle. Already-
   * registered aliases are skipped on subsequent calls.
   *
   * <p><strong>Strict-mode race fix:</strong> {@link registered} is
   * updated only AFTER the buffer is decoded — earlier code marked
   * it before the await, so a mount-2 cache check would see "already
   * loaded" while mount-1's fetch was still in flight, then playMusic
   * found no buffer. If a load is in flight, concurrent callers
   * await the same promise via {@link inFlight}. */
  async loadTheme(theme: Theme): Promise<void> {
    await this.loadEntries(theme.assets?.audio);
  }

  /** Load a set of theme audio entries (a theme's own, or its Royal Road
   *  sounds when the road is on). Same URL rules as {@link loadTheme}. */
  async loadEntries(audioMap: Record<string, ThemeAssetEntry> | undefined): Promise<void> {
    if (!audioMap) return;
    await Promise.all(
      Object.values(audioMap).map((entry) => {
        // Stage 3 — pull the effective URL through the manifest
        // (CDN-versioned when {@code entry.path} resolves, bundled
        // {@code entry.src} otherwise). Returns null when neither
        // can resolve (e.g. CDN-only entry whose pack hasn't been
        // downloaded) — skip silently; BiomePackManager fetches
        // before the player can spin.
        const url = resolveEntryUrl(entry);
        if (!url) return Promise.resolve();
        return this.loadEntry(entry.alias, url);
      }),
    );
  }

  /** Register and load a single audio buffer. Idempotent — repeat calls
   *  for the same alias return the same in-flight promise, then no-op.
   *  Use this for views that need a one-off sample (e.g. lobby music
   *  on the world map) without standing up a fake "theme" wrapper. */
  async loadEntry(alias: string, src: string): Promise<void> {
    if (this.registered.has(alias)) return;
    const existing = this.inFlight.get(alias);
    if (existing) {
      await existing;
      return;
    }
    const ctx = this.ensureContext();
    const promise = (async (): Promise<void> => {
      try {
        const response = await fetch(src);
        const arrayBuffer = await response.arrayBuffer();
        const buffer = await ctx.decodeAudioData(arrayBuffer);
        this.buffers.set(alias, buffer);
        this.registered.add(alias);
        // If a play* call was queued waiting for this alias, fire it.
        this.startPendingIfMatching(alias);
      } catch (err) {
        // Silent failure is OK — missing audio falls back to silence.
        // The console warn lets a dev know to check the theme bundle.
        console.warn(`[AudioBus] failed to load '${alias}'`, err);
      } finally {
        this.inFlight.delete(alias);
      }
    })();
    this.inFlight.set(alias, promise);
    await promise;
  }

  /** In-flight buffer-load promises, keyed by alias. Lets concurrent
   *  {@link loadTheme} / {@link loadEntry} calls await the same fetch
   *  instead of each starting their own (strict-mode-safe). */
  private readonly inFlight = new Map<string, Promise<unknown>>();

  /** Play the buffer registered under {@code alias}. No-op if muted, the
   * context isn't active, or the alias was never loaded. {@code volume}
   * is multiplied with the master volume — pass 0..1. */
  play(alias: string, options?: { volume?: number }): void {
    if (this.muted) return;
    if (!this.sfxAllowed) return;
    if (!this.context || !this.masterGain) return;
    if (this.context.state === 'suspended') return;
    const buffer = this.buffers.get(alias);
    if (!buffer) return;

    const source = this.context.createBufferSource();
    source.buffer = buffer;

    if (options?.volume !== undefined && options.volume !== 1) {
      const gain = this.context.createGain();
      gain.gain.value = options.volume;
      source.connect(gain);
      gain.connect(this.masterGain);
    } else {
      source.connect(this.masterGain);
    }
    source.start();
  }

  /**
   * Loop the buffer registered under {@code alias} as a sound effect (the
   * reels' whirr while they spin), until the returned handle stops it.
   * Same gates as {@link play}: muted, sound effects off, a suspended
   * context or an unloaded alias give a handle that does nothing.
   * {@code loopStart} and {@code loopLength} (seconds) pick the stretch of
   * the file that loops seamlessly, clear of any padding the decoder adds.
   */
  playLoop(alias: string, options: { volume?: number; loopStart?: number; loopLength?: number; fadeInMs?: number } = {}): SfxLoop {
    const silent: SfxLoop = { setVolume: () => undefined, stop: () => undefined };
    if (this.muted || !this.sfxAllowed) return silent;
    if (!this.context || !this.masterGain) return silent;
    if (this.context.state === 'suspended') return silent;
    const buffer = this.buffers.get(alias);
    if (!buffer) return silent;
    const ctx = this.context;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const start = Math.min(options.loopStart ?? 0, buffer.duration);
    source.loopStart = start;
    source.loopEnd = options.loopLength ? Math.min(buffer.duration, start + options.loopLength) : buffer.duration;
    const gain = ctx.createGain();
    const volume = options.volume ?? 1;
    const fadeIn = (options.fadeInMs ?? 0) / 1000;
    gain.gain.setValueAtTime(fadeIn > 0 ? 0 : volume, ctx.currentTime);
    if (fadeIn > 0) gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + fadeIn);
    source.connect(gain);
    gain.connect(this.masterGain);
    source.start(0, start);
    let stopped = false;
    const ramp = (to: number, ms: number) => {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(to, now + Math.max(0.005, ms / 1000));
    };
    return {
      setVolume: (to, ms = 80) => {
        if (!stopped) ramp(Math.max(0, to), ms);
      },
      stop: (fadeOutMs = 120) => {
        if (stopped) return;
        stopped = true;
        ramp(0, fadeOutMs);
        try {
          source.stop(ctx.currentTime + fadeOutMs / 1000 + 0.02);
        } catch {
          // Already stopped.
        }
        source.onended = () => {
          source.disconnect();
          gain.disconnect();
        };
      },
    };
  }

  /** Start looping music. If music is already playing with the same
   * alias, no-op. If a different alias is playing, cross-fade. If the
   * context is still suspended (no user gesture yet), queue the call
   * and start automatically on the next {@link resume}.
   *
   * <p>{@code fadeInMs} ramps the gain up linearly from 0 to
   * {@code volume} over that window. Default volume = 0.6 (matches
   * the design spec's Music bus default).
   */
  playMusic(alias: string, options?: LoopPlayOptions): void {
    this.lastMusicAlias = alias;
    if (!this.musicAllowed) return;
    this.startLoop('music', alias, options ?? {});
  }

  /** Stop the currently-playing music with an optional fade-out. */
  stopMusic(options?: { fadeOutMs?: number }): void {
    this.stopLoop('music', options?.fadeOutMs ?? 0);
  }

  /** Start looping ambient. Same shape as {@link playMusic} — runs in
   * its own channel alongside music. Default volume = 0.5 (Ambient
   * bus default). */
  playAmbient(alias: string, options?: LoopPlayOptions): void {
    this.lastAmbientAlias = alias;
    if (!this.musicAllowed) return;
    this.startLoop('ambient', alias, options ?? {});
  }

  /** Stop the currently-playing ambient with an optional fade-out. */
  stopAmbient(options?: { fadeOutMs?: number }): void {
    this.stopLoop('ambient', options?.fadeOutMs ?? 0);
  }

  /** What's playing in the music channel right now, or {@code null}
   * if nothing is. Includes queued (not-yet-started) music. */
  currentMusic(): string | null {
    return this.currentMusicAlias ?? this.pendingMusic?.alias ?? null;
  }

  /** What's playing in the ambient channel right now. */
  currentAmbient(): string | null {
    return this.currentAmbientAlias ?? this.pendingAmbient?.alias ?? null;
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    if (this.masterGain && this.context) {
      const target = muted ? 0 : this.masterVolume;
      const now = this.context.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
      this.masterGain.gain.linearRampToValueAtTime(target, now + 0.05);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && !this.muted) {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  /** Module-shared default bus — engines that only ever construct one
   * Pixi app share state through this. Tests construct their own. */
  static readonly shared = new AudioBus();

  // ─────────────────── private loop machinery ───────────────────

  /** Common entrypoint for music / ambient. Queues if the context
   * is still suspended; otherwise starts immediately. */
  private startLoop(channel: LoopChannel, alias: string, opts: LoopPlayOptions): void {
    const currentAlias = channel === 'music' ? this.currentMusicAlias : this.currentAmbientAlias;
    const pendingAlias =
      channel === 'music' ? this.pendingMusic?.alias : this.pendingAmbient?.alias;
    if (alias === currentAlias || alias === pendingAlias) return; // same thing already playing / queued
    if (!this.context || this.context.state === 'suspended') {
      // Queue: the next resume() will start it.
      if (channel === 'music') {
        this.pendingMusic = { alias, opts };
      } else {
        this.pendingAmbient = { alias, opts };
      }
      this.ensureContext();
      return;
    }
    this.startLoopNow(channel, alias, opts);
  }

  /** Actually create + start the looping BufferSourceNode + gain ramp.
   * Caller has already verified the context is running. If the buffer
   * isn't loaded yet (e.g. {@link loadTheme} is still fetching),
   * queue the call and let {@link loadTheme} fire it via
   * {@link startPendingIfMatching} once the buffer settles. */
  private startLoopNow(channel: LoopChannel, alias: string, opts: LoopPlayOptions): void {
    if (!this.context || !this.masterGain) return;
    const buffer = this.buffers.get(alias);
    if (!buffer) {
      // Queue — loadTheme will fire us once the buffer is decoded.
      if (channel === 'music') this.pendingMusic = { alias, opts };
      else this.pendingAmbient = { alias, opts };
      return;
    }
    const fadeInMs = opts.fadeInMs ?? 0;
    const targetVolume = opts.volume ?? (channel === 'music' ? 0.6 : 0.5);

    // Cross-fade: schedule the current source's gain to ramp down
    // and stop, in parallel with the new source ramping up. We then
    // replace the channel's source/gain refs with the new pair.
    const currentSource = channel === 'music' ? this.musicSource : this.ambientSource;
    const currentGain = channel === 'music' ? this.musicGain : this.ambientGain;
    if (currentSource && currentGain) {
      this.fadeAndStop(currentSource, currentGain, fadeInMs > 0 ? fadeInMs : 500);
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = this.context.createGain();
    const startTime = this.context.currentTime;
    if (fadeInMs > 0) {
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(targetVolume, startTime + fadeInMs / 1000);
    } else {
      gain.gain.value = targetVolume;
    }
    source.connect(gain);
    gain.connect(this.masterGain);
    source.start();

    if (channel === 'music') {
      this.musicSource = source;
      this.musicGain = gain;
      this.currentMusicAlias = alias;
    } else {
      this.ambientSource = source;
      this.ambientGain = gain;
      this.currentAmbientAlias = alias;
    }
  }

  private stopLoop(channel: LoopChannel, fadeOutMs: number): void {
    if (channel === 'music') {
      this.pendingMusic = null;
      if (this.musicSource && this.musicGain) {
        this.fadeAndStop(this.musicSource, this.musicGain, fadeOutMs);
      }
      this.musicSource = null;
      this.musicGain = null;
      this.currentMusicAlias = null;
    } else {
      this.pendingAmbient = null;
      if (this.ambientSource && this.ambientGain) {
        this.fadeAndStop(this.ambientSource, this.ambientGain, fadeOutMs);
      }
      this.ambientSource = null;
      this.ambientGain = null;
      this.currentAmbientAlias = null;
    }
  }

  /** If a {@link playMusic} / {@link playAmbient} call was queued
   *  waiting for an alias's buffer to load, fire it now. Called from
   *  {@link loadTheme} after each successful decode. */
  private startPendingIfMatching(alias: string): void {
    if (this.pendingMusic?.alias === alias) {
      const { opts } = this.pendingMusic;
      this.pendingMusic = null;
      // Only fire if context is running — if still suspended, leave
      // queued for the next resume() to pick up.
      if (this.context?.state === 'running') {
        this.startLoopNow('music', alias, opts);
      } else {
        this.pendingMusic = { alias, opts };
      }
    }
    if (this.pendingAmbient?.alias === alias) {
      const { opts } = this.pendingAmbient;
      this.pendingAmbient = null;
      if (this.context?.state === 'running') {
        this.startLoopNow('ambient', alias, opts);
      } else {
        this.pendingAmbient = { alias, opts };
      }
    }
  }

  /** Ramp {@code gain} to 0 over {@code fadeOutMs} and stop the source
   * at the end. {@code fadeOutMs = 0} stops immediately. */
  private fadeAndStop(source: AudioBufferSourceNode, gain: GainNode, fadeOutMs: number): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    if (fadeOutMs > 0) {
      const endTime = now + fadeOutMs / 1000;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, endTime);
      try {
        source.stop(endTime);
      } catch {
        // Already stopped — ignore.
      }
    } else {
      try {
        source.stop(now);
      } catch {
        // Already stopped — ignore.
      }
    }
  }
}

type LoopChannel = 'music' | 'ambient';

interface LoopPlayOptions {
  /** Linear ramp-in window in milliseconds (default 0 = instant). */
  fadeInMs?: number;
  /** Target gain (0..1). Defaults: music 0.6, ambient 0.5 per the
   *  design spec's bus levels. */
  volume?: number;
}
